import React from "react";
import {
  Box,
  Group,
  Title,
  Text,
  ActionIcon,
  Button,
  ScrollArea,
  Stack,
  TextInput,
  Select,
  Grid,
  Divider,
  Checkbox,
  Paper,
  SegmentedControl,
  Modal,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Rnd } from "react-rnd";
import { SignatureAnnotation, FieldType } from "../../types";
import { SignerEntry } from "../../lib/signing-metadata";

type EditorStepProps = {
  metadata: { docTitle: string; author: string };
  currentPage: number;
  numPages: number;
  setCurrentPage: (page: number) => void;
  handleStartFinalize: () => void;
  pageThumbs: string[];
  pageImages: string[];
  pdfDimensions: { width: number; height: number }[];
  scale: number;
  scrollViewportRef: React.RefObject<HTMLDivElement>;
  canvasRef: React.RefObject<HTMLDivElement>;
  dragImageRef: React.RefObject<HTMLDivElement>;
  handleCanvasDrop: (e: React.DragEvent) => void;
  annotations: SignatureAnnotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<SignatureAnnotation[]>>;
  selectedFieldId: string | null;
  setSelectedFieldId: (id: string | null) => void;
  editingField: { id: string; location: "canvas" | "sidebar" } | null;
  setEditingField: (
    field: { id: string; location: "canvas" | "sidebar" } | null,
  ) => void;
  signerColors: string[];
  activeColor: string;
  selectedSignerIndex: string;
  setSelectedSignerIndex: (index: string) => void;
  onDragStop: (
    id: string,
    pageIdx: number,
    d: { x: number; y: number },
  ) => void;
  onResizeStop: (
    id: string,
    pageIdx: number,
    ref: HTMLElement,
    position: { x: number; y: number },
  ) => void;
  updateSignerName: (index: number, name: string) => void;
  updateSignerEmail: (index: number, email: string) => void;
  removeSigner: (index: number) => void;
  addSigner: () => void;
  addField: (type: FieldType) => void;
  signers: SignerEntry[];
};

export function EditorStep({
  metadata,
  currentPage,
  numPages,
  setCurrentPage,
  handleStartFinalize,
  pageThumbs,
  pageImages,
  pdfDimensions,
  scale,
  scrollViewportRef,
  canvasRef,
  dragImageRef,
  handleCanvasDrop,
  annotations,
  setAnnotations,
  selectedFieldId,
  setSelectedFieldId,
  editingField,
  setEditingField,
  signerColors,
  activeColor,
  selectedSignerIndex,
  setSelectedSignerIndex,
  onDragStop,
  onResizeStop,
  updateSignerName,
  updateSignerEmail,
  removeSigner,
  addSigner,
  addField,
  signers,
}: EditorStepProps) {
  const [mobileTab, setMobileTab] = React.useState<"document" | "sidebar">("document");
  const [mobileWarningDismissed, setMobileWarningDismissed] = React.useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  return (
    <div className="editor-step-layout">
      {/* Main Canvas Area */}
      <div className="editor-main-area">
        <Box
          p="md"
          style={{
            borderBottom: "1px solid #dee2e6",
            background: "white",
            flexShrink: 0,
          }}
        >
          <Group justify="space-between" align="center" style={{ gap: '12px' }}>
            <Title order={4} style={{ flex: 1, minWidth: 200, wordBreak: 'break-word', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {metadata.docTitle || "Untitled Document"}
            </Title>
            <Group gap="sm" wrap="nowrap">
              <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
                Page {currentPage + 1} of {numPages}
              </Text>
              <Group gap="xs" wrap="nowrap">
                <ActionIcon
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage <= 0}
                >
                  ←
                </ActionIcon>
                <ActionIcon
                  onClick={() =>
                    setCurrentPage(Math.min(numPages - 1, currentPage + 1))
                  }
                  disabled={currentPage >= numPages - 1}
                >
                  →
                </ActionIcon>
              </Group>
              <Button onClick={handleStartFinalize}>Next: Finalize</Button>
            </Group>
          </Group>
        </Box>

        {isMobile && (
          <Box p="xs" bg="gray.1" style={{ borderBottom: "1px solid #dee2e6", flexShrink: 0 }}>
            <SegmentedControl
              fullWidth
              value={mobileTab}
              onChange={(v) => setMobileTab(v as "document" | "sidebar")}
              data={[
                { label: "Document", value: "document" },
                { label: "Settings & Fields", value: "sidebar" }
              ]}
            />
          </Box>
        )}

        <Modal
          opened={!!isMobile && !mobileWarningDismissed}
          onClose={() => setMobileWarningDismissed(true)}
          title="Desktop Recommended"
          centered
        >
          <Text mb="md">
            Document preparation is much easier on a desktop or laptop view. You can proceed on mobile, but placing and adjusting signature fields may be difficult on a small touchscreen.
          </Text>
          <Button fullWidth onClick={() => setMobileWarningDismissed(true)}>
            I understand, continue anyway
          </Button>
        </Modal>

        <div
          className="editor-content-area"
          style={{ display: isMobile && mobileTab !== "document" ? "none" : undefined }}
        >
          <div className="editor-thumbnails">
            <ScrollArea style={{ flex: 1 }} p="xs">
              <Stack gap="md">
                {pageThumbs.map((thumb, i) => (
                  <div
                    key={i}
                    style={{
                      cursor: "pointer",
                      border:
                        i === currentPage
                          ? "2px solid #228be6"
                          : "2px solid transparent",
                      padding: 2,
                    }}
                    onClick={() => setCurrentPage(i)}
                  >
                    <img
                      src={thumb}
                      alt={`P${i + 1}`}
                      style={{ width: "100%", display: "block" }}
                    />
                    <Text size="xs" ta="center">
                      Page {i + 1}
                    </Text>
                  </div>
                ))}
              </Stack>
            </ScrollArea>
          </div>

          <div
            className="editor-canvas-container"
            ref={scrollViewportRef}
            onDragOver={(e: React.DragEvent) => e.preventDefault()}
            onDrop={handleCanvasDrop}
            onClick={() => setSelectedFieldId(null)}
          >
            {pageImages.map((src, pageIdx) => {
              if (pageIdx !== currentPage) return null;
              const dims = pdfDimensions[pageIdx] || {
                width: 600,
                height: 800,
              };
              const pxW = dims.width * scale;
              const pxH = dims.height * scale;

              return (
                <div
                  key={pageIdx}
                  style={{
                    position: "relative",
                    width: pxW,
                    height: pxH,
                    minWidth: pxW,
                    minHeight: pxH,
                    flexShrink: 0,
                    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    background: "white",
                  }}
                  ref={canvasRef}
                >
                  <img
                    src={src}
                    alt={`Page ${pageIdx + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  />

                  {annotations
                    .filter((a) => a.page === pageIdx + 1)
                    .map((ann) => {
                      const isSelected = selectedFieldId === ann.id;
                      const isEditingCanvas =
                        editingField?.id === ann.id &&
                        editingField.location === "canvas";
                      const signerIdx = ann.signerId ?? 0;
                      const color =
                        ann.signerId === null
                          ? "#555"
                          : signerColors[signerIdx % signerColors.length] ||
                          "#000";

                      return (
                        <Rnd
                          key={ann.id}
                          size={{
                            width: ann.width * (72 / 25.4) * scale,
                            height: ann.height * (72 / 25.4) * scale,
                          }}
                          position={{
                            x: ann.x * (72 / 25.4) * scale,
                            y: ann.y * (72 / 25.4) * scale,
                          }}
                          onDragStop={(_e: any, d: any) =>
                            onDragStop(ann.id, currentPage, d)
                          }
                          onResizeStop={(
                            _e: any,
                            _dir: any,
                            ref: HTMLElement,
                            _delValue: any,
                            pos: any,
                          ) => onResizeStop(ann.id, currentPage, ref, pos)}
                          onDragStart={(e: any) => {
                            if (isEditingCanvas) {
                              e.stopPropagation();
                              return false;
                            }
                            setSelectedFieldId(ann.id);
                          }}
                          bounds="parent"
                          disableDragging={isEditingCanvas}
                          enableResizing={!isEditingCanvas}
                          style={{
                            position: "absolute",
                            borderRadius: "4px",
                            border: isSelected
                              ? `2px solid ${color}`
                              : `1px solid ${color}`,
                            backgroundColor: isSelected
                              ? "white"
                              : `${color}15`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: isSelected ? 30 : 10,
                            boxShadow: isSelected
                              ? "0 4px 12px rgba(0,0,0,0.15)"
                              : "none",
                          }}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (!isEditingCanvas) setSelectedFieldId(ann.id);
                          }}
                        >
                          {isSelected && !isEditingCanvas && (
                            <Group
                              gap={4}
                              style={{
                                position: "absolute",
                                top: -30,
                                right: 0,
                                background: "white",
                                border: "1px solid #ccc",
                                padding: 2,
                                borderRadius: 4,
                                zIndex: 40,
                              }}
                            >
                              <ActionIcon
                                variant="subtle"
                                size="xs"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  setEditingField({
                                    id: ann.id,
                                    location: "canvas",
                                  });
                                }}
                              >
                                <Text size="xs">✎</Text>
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                size="xs"
                                color="red"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  setAnnotations((prev) =>
                                    prev.filter((x) => x.id !== ann.id),
                                  );
                                  setSelectedFieldId(null);
                                }}
                              >
                                ×
                              </ActionIcon>
                            </Group>
                          )}
                          {isEditingCanvas ? (
                            <TextInput
                              size="xs"
                              autoFocus
                              styles={{
                                input: {
                                  height: ann.height * (72 / 25.4) * scale - 4,
                                  minHeight: 0,
                                  padding: 2,
                                  textAlign: "center",
                                  fontSize: `${12 * scale}px`,
                                  cursor: "text",
                                },
                              }}
                              defaultValue={
                                ann.labelText ||
                                (ann.type === "name"
                                  ? "Name"
                                  : ann.type.toUpperCase())
                              }
                              onBlur={(
                                e: React.FocusEvent<HTMLInputElement>,
                              ) => {
                                setAnnotations((prev) =>
                                  prev.map((a) =>
                                    a.id === ann.id
                                      ? { ...a, labelText: e.target.value }
                                      : a,
                                  ),
                                );
                                setEditingField(null);
                              }}
                              onFocus={(
                                e: React.FocusEvent<HTMLInputElement>,
                              ) => e.target.select()}
                              onKeyDown={(
                                e: React.KeyboardEvent<HTMLInputElement>,
                              ) => {
                                if (e.key === "Enter")
                                  (e.target as HTMLInputElement).blur();
                                if (e.key === "Escape") setEditingField(null);
                              }}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                              }}
                              onMouseDown={(e: React.MouseEvent) =>
                                e.stopPropagation()
                              }
                            />
                          ) : (
                            <Text
                              size="xs"
                              fw={isSelected ? 700 : 500}
                              c={color}
                              ta="center"
                              style={{
                                fontSize: `${12 * scale}px`,
                                userSelect: "none",
                              }}
                            >
                              {ann.labelText ||
                                (ann.type === "name"
                                  ? "Name"
                                  : ann.type === "initials"
                                    ? "Initials"
                                    : ann.type === "date-now"
                                      ? "Date Now"
                                      : ann.type === "text"
                                        ? "Text"
                                        : ann.type.toUpperCase())}
                              {ann.optional && (
                                <span
                                  style={{
                                    fontSize: "7px",
                                    opacity: 0.7,
                                    marginLeft: 2,
                                  }}
                                >
                                  (Opt)
                                </span>
                              )}
                            </Text>
                          )}
                        </Rnd>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sidebar Area */}
      <div
        className="editor-sidebar"
        style={{ display: isMobile && mobileTab !== "sidebar" ? "none" : undefined }}
      >
        <Box p="md" style={{ borderBottom: "1px solid #eee" }}>
          <Text fw={700} mb="xs">
            Signers
          </Text>
          <Stack gap="xs">
            {signers.map((s, i) => (
              <Group key={i} gap="xs" align="flex-start">
                <Box
                  w={12}
                  h={12}
                  mt={8}
                  bg={signerColors[i % signerColors.length]}
                  style={{ borderRadius: 2 }}
                />
                <Stack gap={4} style={{ flex: 1 }}>
                  <TextInput
                    value={s.name || ""}
                    onChange={(e) => updateSignerName(i, e.target.value)}
                    placeholder="Signer Name"
                    size="xs"
                  />
                  <TextInput
                    value={s.email}
                    onChange={(e) => updateSignerEmail(i, e.target.value)}
                    placeholder="Email Address"
                    size="xs"
                  />
                </Stack>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  size="xs"
                  mt={4}
                  onClick={() => removeSigner(i)}
                >
                  ×
                </ActionIcon>
              </Group>
            ))}
            <Button variant="subtle" size="xs" onClick={addSigner}>
              + Add Signer
            </Button>
          </Stack>
        </Box>

        <Box p="md" style={{ flex: 1 }}>
          <Text fw={700} mb="xs">
            Fields
          </Text>
          <Select
            value={selectedSignerIndex}
            onChange={(v) => setSelectedSignerIndex(v || "0")}
            data={[
              ...signers.map((s, i) => ({
                value: String(i),
                label: s.name
                  ? `${s.name} (${s.email})`
                  : s.email || `Signer ${i + 1}`,
              })),
              { value: "label", label: "Read-only Label" },
            ]}
            size="xs"
            mb="sm"
          />

          <Grid gutter="xs" mb="md">
            {[
              { t: "signature", l: "Signature" },
              { t: "initials", l: "Initials" },
              { t: "name", l: "Name" },
              { t: "text", l: "Text" },
              { t: "date-now", l: "Date Now" },
              { t: "date", l: "Date" },
              { t: "checkbox", l: "Checkbox" },
              { t: "label", l: "Static Label" },
            ]
              .filter((b) =>
                selectedSignerIndex === "label"
                  ? b.t === "label"
                  : b.t !== "label",
              )
              .map((b) => (
                <Grid.Col span={6} key={b.t}>
                  <Button
                    variant="light"
                    h={50}
                    fullWidth
                    style={{
                      border: `1px solid ${activeColor}`,
                      color: activeColor,
                      backgroundColor: `${activeColor}15`,
                    }}
                    onClick={() => {
                      addField(b.t as FieldType);
                      if (isMobile) setMobileTab("document");
                    }}
                    draggable
                    onDragStart={(e: React.DragEvent) => {
                      e.dataTransfer.setData("fieldType", b.t as string);
                      if (dragImageRef.current) {
                        // Prepare the drag image size
                        let wMm = 35;
                        let hMm = 12;
                        if (b.t === "initials") {
                          wMm = 18;
                          hMm = 12;
                        } else if (b.t === "checkbox") {
                          wMm = 8;
                          hMm = 8;
                        } else if (b.t !== "signature") {
                          hMm = 8;
                        }

                        dragImageRef.current.style.width = `${wMm * (72 / 25.4) * scale}px`;
                        dragImageRef.current.style.height = `${hMm * (72 / 25.4) * scale}px`;
                        dragImageRef.current.style.borderColor = activeColor;
                        dragImageRef.current.innerText = b.l.toUpperCase();
                        // Offset ghost to mouse center
                        e.dataTransfer.setDragImage(
                          dragImageRef.current,
                          (wMm * (72 / 25.4) * scale) / 2,
                          (hMm * (72 / 25.4) * scale) / 2,
                        );
                      }
                    }}
                  >
                    <Stack gap={0} align="center">
                      <Text size="xs" fw={600}>
                        {b.l}
                      </Text>
                      <Text size="10px" opacity={0.6}>
                        Drag/Click
                      </Text>
                    </Stack>
                  </Button>
                </Grid.Col>
              ))}
          </Grid>

          <Divider my="sm" />
          <Text size="sm" fw={600} mb="xs">
            Page {currentPage + 1} Fields
          </Text>
          <ScrollArea h={300}>
            {annotations
              .filter((a) => a.page === currentPage + 1)
              .map((ann) => (
                <Paper
                  key={ann.id}
                  p="xs"
                  withBorder
                  mb="xs"
                  radius="sm"
                  bg={ann.id === selectedFieldId ? "blue.0" : "gray.0"}
                  style={{
                    cursor: "pointer",
                    borderLeft:
                      ann.id === selectedFieldId
                        ? `4px solid ${activeColor}`
                        : undefined,
                  }}
                  onClick={() => setSelectedFieldId(ann.id)}
                >
                  <Stack gap={4}>
                    <Group justify="space-between">
                      <Text size="10px" fw={700} c="dimmed">
                        {ann.type.toUpperCase()}
                      </Text>
                      <ActionIcon
                        size="xs"
                        color="red"
                        variant="transparent"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setAnnotations((prev) =>
                            prev.filter((x) => x.id !== ann.id),
                          );
                        }}
                      >
                        ×
                      </ActionIcon>
                    </Group>
                    {editingField?.id === ann.id &&
                      editingField.location === "sidebar" ? (
                      <TextInput
                        size="xs"
                        autoFocus
                        defaultValue={
                          ann.labelText || (ann.type === "name" ? "Name" : "")
                        }
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                          setAnnotations((prev) =>
                            prev.map((a) =>
                              a.id === ann.id
                                ? { ...a, labelText: e.target.value }
                                : a,
                            ),
                          );
                          setEditingField(null);
                        }}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                          e.target.select()
                        }
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                          e.key === "Enter" &&
                          (e.target as HTMLInputElement).blur()
                        }
                      />
                    ) : (
                      <Group
                        gap={4}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setEditingField({ id: ann.id, location: "sidebar" });
                        }}
                      >
                        <Text size="xs" style={{ flex: 1 }}>
                          {ann.labelText ||
                            (ann.type === "name" ? "Name Field" : ann.type)}
                        </Text>
                        <ActionIcon variant="subtle" size="xs">
                          ✎
                        </ActionIcon>
                      </Group>
                    )}
                    {ann.type !== "label" && (
                      <Checkbox
                        label="Optional"
                        size="xs"
                        checked={ann.optional || false}
                        onChange={(e) =>
                          setAnnotations((prev) =>
                            prev.map((a) =>
                              a.id === ann.id
                                ? { ...a, optional: e.currentTarget.checked }
                                : a,
                            ),
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </Stack>
                </Paper>
              ))}
          </ScrollArea>
        </Box>
      </div>

      <div
        ref={dragImageRef}
        style={{
          position: "fixed",
          top: -1000,
          left: -1000,
          border: "1px solid black",
          borderRadius: "4px",
          backgroundColor: "rgba(255,255,255,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          fontSize: "10px",
          fontWeight: 600,
          zIndex: -1,
        }}
      />
    </div>
  );
}
